import React, { useEffect } from 'react';
import styled from 'styled-components';
import DeleteBin from 'utils/images/bin.svg';
import * as Yup from 'yup';
import useChildEditor from '../../hooks/use-child-editor';
import {
  requiredNameWithMaxLength,
  requiredOptionsPrice,
  requiredStatus,
  requiredType,
} from '../../utils/validation';
import AddButton from '../shared/AddButton';
import Alert from '../shared/Alert';
import Checkbox from '../shared/Checkbox';
import EditorForm from '../shared/EditorForm';
import EditorRow from '../shared/EditorRow';
import LimitSelect from '../shared/LimitSelect';
import NameField from '../shared/NameField';
import OptionsField from '../shared/OptionsField';
import VariantTypes from '../shared/VariantTypes';

const validation = Yup.object({
  name: requiredNameWithMaxLength,
  status: requiredStatus,
  type: requiredType,
  children: Yup.array().of(
    Yup.object().shape({
      price: requiredOptionsPrice,
      name: requiredNameWithMaxLength,
    })
  ),
});

const EditorContainer = styled.div`
  border: 1px solid #dedede;
  padding-bottom: 25px;
`;

const OptionsFieldsContainer = styled.div`
  overflow: auto;
`;

const AddButtonContainer = styled.div`
  margin: 20px 0 0;
`;

const DeleteContainer = styled.div`
  float: right;
  margin: 15px 15px 0 0;
`;

const DeleteIcon = styled.img`
  width: 14px;
  cursor: pointer;
`;

const LimitSelectResized = styled(LimitSelect)`
  width: fit-content;
  margin-right: 30px;
  display: inline-block;
`;

const FlexEditorRow = styled(EditorRow)`
  display: flex;
  align-items: flex-end;
`;

const RequiredCheckbox = styled(Checkbox)`
  width: fit-content;
  margin-bottom: 0px;
  display: flex;
  height: 35px;
`;

function VariantEditor(props) {
  const { variant, error, onChange, onRemove, onEditorSetup } = props;

  const { item, onAddOption, onChangeOption, onFieldChange, onRemoveOption } =
    useChildEditor({
      init: variant,
      validation,
      onEditorSetup,
      onChange,
    });

  const onNameChange = onFieldChange('name');
  const onLimitChange = onFieldChange('choice_limit');
  const onRequiredChange = onFieldChange('required');

  const handleAddOption = () =>
    onAddOption({
      name: '',
      price: 0,
      type: 'option',
      status: 'live',
    });
  const handleRemove = () => onRemove(item);

  const showLimit = item.variantType === 'multiple';

  const onVariantTypeChange = e => {
    if (showLimit && e.target.value === 'single') {
      // reset choice_limit on transition to single variantType
      onChange({
        ...item,
        variantType: 'single',
        choice_limit: 0,
        children: (item.children || []).map(option => {
          return { ...option, choice_limit: 0 };
        }),
      });
    } else {
      onFieldChange('variantType')(e);
    }
  };

  useEffect(() => {
    if ((item.children || []).length < 1) {
      handleAddOption();
    }
  }, []);

  const handleRequiredChanged = (event, checked) => {
    onRequiredChange({ target: { value: checked } });
  };

  return (
    <EditorContainer>
      <DeleteContainer>
        <DeleteIcon src={DeleteBin} alt="Delete" onClick={handleRemove} />
      </DeleteContainer>

      <EditorForm>
        <NameField
          id={`variant-name-${item.id}`}
          type="variant"
          label="Title"
          value={item.name || ''}
          placeholder="e.g. Mixers, Extra Toppings or Sides"
          onChange={onNameChange}
        />

        <FlexEditorRow>
          <VariantTypes
            id={`variant-type-select-${item.id}`}
            value={item.variantType}
            onChange={onVariantTypeChange}
          />
          {showLimit ? (
            <LimitSelectResized
              id={`limit-${item.id}`}
              onChange={onLimitChange}
              value={item.choice_limit}
            />
          ) : null}

          <RequiredCheckbox
            onSelect={handleRequiredChanged}
            value="Required"
            id={item.id}
            key={`item-required-${item.id}`}
            isChecked={item.required}
          />
        </FlexEditorRow>

        <OptionsFieldsContainer>
          {item.children.map((option, idx) => (
            <OptionsField
              key={`option-${option.id}`}
              id={`option-${option.id}`}
              option={option}
              hideLabel={idx !== 0}
              disableDelete={idx === 0}
              showLimit={showLimit}
              onChange={onChangeOption}
              onDelete={() => onRemoveOption(option)}
            />
          ))}
        </OptionsFieldsContainer>

        <AddButtonContainer>
          <AddButton onClick={handleAddOption}>Add another choice</AddButton>
        </AddButtonContainer>
      </EditorForm>
      {error ? <Alert>{error}</Alert> : null}
    </EditorContainer>
  );
}

export default VariantEditor;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/ProductList/variant-editor.jsx